import { Course, CourseAnalytics, Lesson, Unit } from "../../../Types/Courses";
import Button from "../../../UI/Button/Button";
import InputField from "../../../UI/Form/InputField";

interface CourseStructurePanelProps {
  id?: string;
  loading: boolean;
  course: Course | null;
  courseStats: CourseAnalytics | null;
  selectedUnitId: number | null;
  selectedLessonId?: number;
  newUnitTitle: string;
  newLessonTitle: string;
  onNewUnitTitleChange: (value: string) => void;
  onNewLessonTitleChange: (value: string) => void;
  onCreateUnit: () => void;
  onCreateLesson: () => void;
  onSelectUnit: (unitId: number) => void;
  onSelectLesson: (unitId: number, lesson: Lesson) => void;
  onGoToCourseDetails: () => void;
  onReorderUnit: (direction: "up" | "down", unit: Unit) => void;
  onRenameUnit: (unit: Unit) => void;
  onDeleteUnit: (unit: Unit) => void;
  onReorderLesson: (direction: "up" | "down", lesson: Lesson) => void;
  onDeleteLesson: (lesson: Lesson) => void;
}

export default function CourseStructurePanel(props: CourseStructurePanelProps) {
  const {
    id,
    loading,
    course,
    courseStats,
    selectedUnitId,
    selectedLessonId,
    newUnitTitle,
    newLessonTitle,
    onNewUnitTitleChange,
    onNewLessonTitleChange,
    onCreateUnit,
    onCreateLesson,
    onSelectUnit,
    onSelectLesson,
    onGoToCourseDetails,
    onReorderUnit,
    onRenameUnit,
    onDeleteUnit,
    onReorderLesson,
    onDeleteLesson,
  } = props;

  return (
    <section className="bg-white rounded-xl shadow border border-gray-100 p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Structure pédagogique</h3>
          <p className="text-xs text-gray-500">
            Crée des unités et organise les leçons avec un parcours clair.
          </p>
        </div>
        {id ? <Button onClick={onGoToCourseDetails}>Détails du cours</Button> : null}
      </div>

      {courseStats ? (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-gray-50 rounded p-2 text-sm">
            <p className="text-xs text-gray-500">Étudiants inscrits</p>
            <p className="font-semibold">{courseStats.enrolled_students}</p>
          </div>
          <div className="bg-gray-50 rounded p-2 text-sm">
            <p className="text-xs text-gray-500">Taux complétion</p>
            <p className="font-semibold">{courseStats.completion_rate}%</p>
          </div>
        </div>
      ) : null}

      <div className="rounded-lg border border-gray-100 p-3 mb-4">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Nouvelle unité</p>
        <div className="flex gap-2">
          <InputField
            placeholder="Nom de la nouvelle unité"
            value={newUnitTitle}
            onChange={(event) => onNewUnitTitleChange(event.target.value)}
          />
          <Button color="primary" onClick={onCreateUnit}>
            + Unité
          </Button>
        </div>
      </div>

      {loading ? <p className="text-sm text-gray-500">Chargement...</p> : null}

      <div className="space-y-3">
        {course?.units?.map((unit) => (
          <article
            key={unit.id}
            className={`rounded-lg border p-3 ${
              selectedUnitId === unit.id ? "border-primary bg-primary/5" : "border-gray-200"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                className="text-left font-medium hover:underline"
                onClick={() => onSelectUnit(unit.id)}
              >
                {unit.title}
              </button>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => onReorderUnit("up", unit)}>↑</Button>
                <Button onClick={() => onReorderUnit("down", unit)}>↓</Button>
                <Button onClick={() => onRenameUnit(unit)}>Renommer</Button>
                <Button color="error" onClick={() => onDeleteUnit(unit)}>
                  Supprimer
                </Button>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              {(unit.lessons || []).map((lesson) => (
                <div
                  key={lesson.id}
                  className={`rounded border p-2 flex items-center justify-between ${
                    selectedLessonId === lesson.id ? "border-blue-500 bg-blue-50" : "border-gray-200"
                  }`}
                >
                  <button
                    className="text-left"
                    onClick={() => {
                      onSelectLesson(unit.id, lesson);
                    }}
                  >
                    <span className="font-medium">{lesson.title}</span>
                    <span className="text-xs text-gray-500 ml-2">({lesson.type})</span>
                  </button>
                  <div className="flex gap-1">
                    <Button onClick={() => onReorderLesson("up", lesson)}>↑</Button>
                    <Button onClick={() => onReorderLesson("down", lesson)}>↓</Button>
                    <Button color="error" onClick={() => onDeleteLesson(lesson)}>
                      X
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {selectedUnitId === unit.id ? (
              <div className="flex gap-2 mt-3">
                <InputField
                  placeholder="Titre de la leçon"
                  value={newLessonTitle}
                  onChange={(event) => onNewLessonTitleChange(event.target.value)}
                />
                <Button color="info" onClick={onCreateLesson}>
                  + Leçon
                </Button>
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}
